import {
  IServiceAuditorFinding,
  IServiceAuditorPreviousReview,
  IServiceAuditorPreviousRound,
  IServiceAuditorPreviousRoundFinding,
  IServiceAuditorReport,
  ServiceAuditorFindingVerdict,
  ServiceAuditorPreviousOutcome,
  ServiceAuditorPreviousUnsettledReason,
  ServiceAuditorReviewerParty,
} from '@commenda/api/agent-platform/service-auditor/types';

import { ServiceAuditFeedbackHelper } from './ServiceAuditFeedbackHelper';

export type TServiceAuditPreviousOutcomeListKey = keyof Pick<
  IServiceAuditorPreviousRound,
  'fixed' | 'notAMistake' | 'unsettled'
>;

export interface IServiceAuditPreviousRoundLine {
  key: string;
  text: string;
}

export interface IServiceAuditPreviousOutcomeItem {
  key: string;
  headline: string;
  detailLines: IServiceAuditPreviousRoundLine[];
  // Marked Real in an earlier round; a fixed one opens its section by default.
  isPreviouslyMarkedReal: boolean;
}

export class ServiceAuditPreviousRoundHelper {
  static readonly UNSETTLED_REASONS: Readonly<
    Record<ServiceAuditorPreviousUnsettledReason, string>
  > = {
    [ServiceAuditorPreviousUnsettledReason.NO_OUTCOME]:
      'The auditor did not say what happened to it.',
    [ServiceAuditorPreviousUnsettledReason.CONFLICTING_OUTCOMES]:
      'The auditor gave it more than one answer.',
    [ServiceAuditorPreviousUnsettledReason.LINKED_FINDING_MISSING]:
      'The auditor said it is still there, but did not point to a finding in this report.',
    [ServiceAuditorPreviousUnsettledReason.LINKED_FINDING_REFUTED]:
      'The auditor said it is still there, but the matching finding did not pass the double check, so it is not shown.',
    [ServiceAuditorPreviousUnsettledReason.LINK_NOT_OVERLAPPING]:
      'The auditor said it is still there, but pointed to a finding about something else.',
    [ServiceAuditorPreviousUnsettledReason.FIX_NOT_QUOTED]:
      'The auditor said it was fixed, but gave no quote from the draft that shows the fix.',
    [ServiceAuditorPreviousUnsettledReason.NO_REASON]:
      'The auditor said it was not a mistake, but gave no reason.',
  };

  // One line per last-round finding this one repeats, keyed by that finding; a verdict is a hint and never applied.
  static getHintLines(
    finding: Readonly<IServiceAuditorFinding>
  ): IServiceAuditPreviousRoundLine[] {
    return finding.previousFindings.map((previous) => {
      const sameAs = `Same as last round: ${previous.headline}`;
      return {
        key: previous.findingId,
        text: previous.review
          ? `${sameAs}. ${ServiceAuditPreviousRoundHelper.getReviewSentence(previous.review, '')}`
          : sameAs,
      };
    });
  }

  static getOutcomeItems(
    report: Readonly<Pick<IServiceAuditorReport, 'previousRound'>>,
    listKey: TServiceAuditPreviousOutcomeListKey
  ): IServiceAuditPreviousOutcomeItem[] {
    return (report.previousRound?.[listKey] ?? []).map((outcome) => ({
      key: outcome.findingId,
      headline: outcome.headline,
      detailLines: ServiceAuditPreviousRoundHelper.getDetailLines(outcome),
      isPreviouslyMarkedReal:
        outcome.previousReview?.verdict === ServiceAuditorFindingVerdict.REAL,
    }));
  }

  // The unsettled reason first, then the auditor's quote or reason, and the earlier verdict.
  private static getDetailLines(
    outcome: Readonly<IServiceAuditorPreviousRoundFinding>
  ): IServiceAuditPreviousRoundLine[] {
    const { unsettledReason, draftQuote, reason, previousReview } = outcome;
    const lines: IServiceAuditPreviousRoundLine[] = [];
    if (unsettledReason) {
      lines.push({
        key: 'unsettled',
        text: ServiceAuditPreviousRoundHelper.UNSETTLED_REASONS[
          unsettledReason
        ],
      });
    }
    if (outcome.outcome === ServiceAuditorPreviousOutcome.FIXED && draftQuote) {
      lines.push({ key: 'quote', text: `"${draftQuote}"` });
    }
    if (
      outcome.outcome === ServiceAuditorPreviousOutcome.NOT_A_MISTAKE &&
      reason
    ) {
      lines.push({ key: 'reason', text: reason });
    }
    if (previousReview) {
      lines.push({
        key: 'review',
        text: ServiceAuditPreviousRoundHelper.getReviewSentence(
          previousReview,
          ' last round'
        ),
      });
    }
    return lines;
  }

  // "Sam Reviewer (Commenda) marked it Real: reason"; a verdict carried from the round before says so.
  private static getReviewSentence(
    review: Readonly<IServiceAuditorPreviousReview>,
    roundSuffix: string
  ): string {
    const who = ServiceAuditPreviousRoundHelper.getReviewerName(review);
    const verdict = ServiceAuditFeedbackHelper.getVerdictBadge(
      review.verdict
    ).label;
    const marked = review.isInherited
      ? `${who} marked the same mistake ${verdict} two rounds back`
      : `${who} marked it ${verdict}${roundSuffix}`;
    const reason = review.reason?.trim();
    return reason ? `${marked}: ${reason}` : `${marked}.`;
  }

  private static getReviewerName(
    review: Readonly<IServiceAuditorPreviousReview>
  ): string {
    const name = review.reviewedByName?.trim();
    switch (review.reviewerParty) {
      case ServiceAuditorReviewerParty.COMMENDA:
        return name ? `${name} (Commenda)` : 'A Commenda reviewer';
      case ServiceAuditorReviewerParty.VENDOR:
        return name ? `${name} (vendor)` : 'The vendor';
      default:
        return name || 'A reviewer';
    }
  }
}
